import React, { useEffect } from 'react';
import { Link, Route, Switch, matchPath, withRouter } from 'react-router-dom';
import { Tabs, Tab, Box } from '@mui/material';
import LibraryIcon from '@mui/icons-material/Book';
import ReportsIcon from '@mui/icons-material/Assignment';
import DataIcon from '@mui/icons-material/InsertChart';
import ListIcon from '@mui/icons-material/ViewList';
import TimerIcon from '@mui/icons-material/Timer';
import RoomIcon from '@mui/icons-material/Room';
import PhotosIcon from '@mui/icons-material/PhotoLibrary';
import PageTitle from 'global/components/PageTitle';
import NavToTop from 'feature/nav/NavToTop';
import ReportsFilters from 'feature/report/Filters';
import TasksFilters from 'feature/task/Filters';
import TabContainer from 'global/components/TabContainer';
import { TasksIcon } from 'global/components/PerformIcons';
import DocumentList from 'feature/document/DocumentList';
import EditionList from 'feature/document/EditionList';
import TaskList from 'feature/task/TaskList/TaskList';
import Document from 'feature/document/Document';
import ReportList from 'feature/report/ReportList';
import ReportData from 'feature/report/ReportData';
import ReportPhotos from 'feature/report/ReportPhotos';
import TaskListAging from 'feature/task/TaskListAging';
import { TaskListOptions, TaskListAgingOptions, ReportListOptions } from 'global/lib/sort';
import useStyles from './Content.styles';
import { CenterContent, LeftSideBar, RightSideBar, GridContainer } from 'global/components/perform-grid';
import { useVendors } from 'global/hooks/useVendors';
import PermissionGate from 'global/components/permission-gate';
import { useScreen } from 'global/styles';
import { CourseList } from '../../document/CourseList';
import { CourseEditionList } from '../../document/CourseEditionList';
import { CourseDocument } from '../../document/CourseDocument';
import TaskListSC from '../../task/TaskListSC';

export const PerformEditionPage = PermissionGate((props) => {
  useEffect(() => {
    props.changeTitle(<PageTitle Icon={LibraryIcon} title="Library" />);
  }, []);

  return <PerformEditionList {...props} />;
});

export const PerformCoursesPage = PermissionGate((props) => {
  useEffect(() => {
    props.changeTitle(<PageTitle Icon={LibraryIcon} title="Courses" />);
  }, []);

  return <PerformCoursesList {...props} />;
});

export const PerformCourseEditionsPage = PermissionGate((props) => {
  const sidebar = matchPath(props.location.pathname, { path: '/courses/:id/document/:document', exact: true, strict: true });

  useEffect(() => {
    props.changeTitle(<PageTitle Icon={LibraryIcon} title="Courses" />);
  }, []);

  return (
    <>
      <PerformCourseEditionList isDocumentPath={sidebar == null ? false : true} />
      <Route path="/courses/:id/document/:document" component={CourseDocument} />
    </>
  );
});

export const PerformLibraryPage = PermissionGate((props) => {
  const sidebar = matchPath(props.location.pathname, { path: '/library/:library/document/:document', exact: true, strict: true });

  useEffect(() => {
    props.changeTitle(<PageTitle Icon={LibraryIcon} title="Library" />);
  }, []);

  return (
    <>
      <PerformLibraryList isDocumentPath={sidebar == null ? false : true} />
      <Route path="/library/:library/document/:document" component={Document} />
    </>
  );
});

export const PerformReportsPage = PermissionGate((props) => {
  const classes = useStyles();
  const vendors = useVendors(undefined, { includeUsersRealm: true, include: 'vendor', includeBuilders: true });

  const { isMobile } = useScreen();
  const { isUp } = useScreen('xl')

  useEffect(() => {
    props.changeTitle(<PageTitle Icon={ReportsIcon} title="Assessments" />);
  }, []);

  useEffect(() => {
    if (props.location.pathname === '/reports') {
      props.history.replace(`reports/list`);
    }
  }, [props.location]);

  const isData = props.match.params.display == 'data';

  return (
    <GridContainer>
      <LeftSideBar hidden={isMobile} boxProps={{ style: { zIndex: 1000 } }}>
        <ReportsFilters {...props.match.params} vendors={vendors} />
      </LeftSideBar>

      <CenterContent ignoreRight={isData}>
        <NavToTop />
        <h2 className={classes.title}>
          <ReportsIcon className={classes.icon} />
          Assessments
        </h2>
        <Tabs value={props.match.params.display || 'list'} centered indicatorColor="secondary" textColor="secondary" style={isData && isUp ? {marginRight: 'calc(100% / 3)'} : undefined}>
          <Tab component={Link} to={`${'list'}`} icon={<ListIcon />} label={'Browse'} value={'list'} key={0} />
          <Tab component={Link} to={`${'data'}`} icon={<DataIcon />} label={'Data'} value={'data'} key={1} />
          <Tab component={Link} to={`${'photo'}`} icon={<PhotosIcon />} label={'Photos'} value={'photo'} key={2} />
        </Tabs>
        <Switch>
          <Route
            exact
            path="/reports/list"
            render={(match) => (
              <TabContainer key={0}>
                <PerformReportsList sort={{ options: ReportListOptions, showNone: false }} vendors={vendors} {...props.match.params} />
              </TabContainer>
            )}
          />
          <Route
            exact
            path="/reports/data"
            render={(match) => (
              <TabContainer key={1}>
                <ReportData vendors={vendors} {...props.match.params} />
              </TabContainer>
            )}
          />
          <Route
            exact
            path="/reports/photo"
            render={(match) => (
              <TabContainer key={2}>
                <PerformReportsPhotos {...props.match.params} />
              </TabContainer>
            )}
          />
        </Switch>
      </CenterContent>

      <RightSideBar />
    </GridContainer>
  );
});

export const PerformTasksPage = PermissionGate((props) => {
  const classes = useStyles();
  const vendors = useVendors(undefined, { includeUsersRealm: true, include: 'vendor' });

  useEffect(() => props.changeTitle(<PageTitle Icon={TasksIcon} title="Tasks" />), []);

  useEffect(() => {
    if (props.location.pathname === '/tasks') {
      props.history.replace(`tasks/list`);
    }
  }, [props.location]);

  const { isMobile } = useScreen();
  const { isUp } = useScreen('xl')
  const isListView = props.match.params.display === 'scorecard';

  return (
    <GridContainer>
      <LeftSideBar hidden={isMobile}>
        <TasksFilters {...props.match.params} vendors={vendors} />
      </LeftSideBar>

      <CenterContent ignoreRight={isListView}>
        <NavToTop />
        <h2 className={classes.title}>
          <TasksIcon className={classes.icon} />
          Tasks
        </h2>

        <Box displayPrint="none" style={{marginRight:(isListView && isUp ? "calc(100% / 3)" : undefined)}}>
          <Tabs value={props.match.params.display || 'list'} centered indicatorColor="secondary" textColor="secondary">
            <Tab component={Link} to={`${'/tasks/list'}`} icon={<RoomIcon />} label={'By Location'} value={'list'} key={0} />
            <Tab component={Link} to={`${"/tasks/scorecard"}`} icon={<ListIcon />} label={"List"} value={"scorecard"} key={2}/>
          </Tabs>
        </Box>
        <Switch>
          <Route
            path="/tasks/list"
            render={(m) => (
              <TabContainer key={0}>
                <PerformTasksList vendors={vendors} sort={{ options: TaskListOptions }} {...props.match.params} />
              </TabContainer>
            )}
          />
          <Route
            exact
            path="/tasks/age"
            render={(m) => (
              <TabContainer key={1}>
                <PerformTasksAgingList sort={{ options: TaskListAgingOptions }} print={true} vendors={vendors} {...props.match.params} />
              </TabContainer>
            )}
          />
          <Route
            exact
            path="/tasks/scorecard"
            render={(m) => (
              <TabContainer key={2}>
                <PerformTaskListSC sort={{ options: TaskListAgingOptions }} print={true} vendors={vendors} {...props.match.params} />
              </TabContainer>
            )}
          />
        </Switch>
      </CenterContent>

      <RightSideBar />
    </GridContainer>
  );
});

const PerformCoursesList = withRouter(CourseList);
const PerformCourseEditionList = withRouter(CourseEditionList);
const PerformEditionList = withRouter(EditionList);
const PerformLibraryList = withRouter(DocumentList);
const PerformReportsList = withRouter(ReportList);
const PerformTasksList = withRouter(TaskList);
const PerformTaskListSC = withRouter(TaskListSC);
const PerformTasksAgingList = withRouter(TaskListAging);
const PerformReportsPhotos = withRouter(ReportPhotos);
